import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Textarea } from '@/components/ui/textarea';
import { samhengiStorageKey } from '@/lib/samhengiStorage';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

const MAX_CHARS = 2000;
// Truncation notice stays up long enough to be noticed but doesn't linger.
const PASTE_NOTICE_TIMEOUT_MS = 5000;
const STORAGE_DEBOUNCE_MS = 300;

interface SamhengiPanelProps {
  sessionId?: string;
  onChange?: (text: string) => void;
}

/**
 * Inline collapsible panel for ephemeral, session-scoped clinician context
 * (PMH, current meds, today's complaints) that gets prepended to the summary
 * prompt. Stored in sessionStorage only — never sent to the DB — and wiped
 * by AuthContext on cross-user transitions.
 */
export const SamhengiPanel = ({ sessionId, onChange }: SamhengiPanelProps) => {
  const labelId = useId();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [pasteNotice, setPasteNotice] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noticeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hydrate from sessionStorage when the session id resolves. Re-hydrates if
  // the user navigates to a different session within the same tab. onChange is
  // intentionally excluded from deps — re-running on every parent rerender
  // would overwrite the user's in-progress edits with the stored value.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    // Cancel any debounce/notice pending against the previous session so a
    // stale write can't land in the new session's storage key and a stale
    // paste-truncation notice can't appear over fresh text.
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (noticeTimeoutRef.current) {
      clearTimeout(noticeTimeoutRef.current);
      noticeTimeoutRef.current = null;
    }
    setPasteNotice(false);
    const key = samhengiStorageKey(sessionId);
    if (!key) {
      setText('');
      onChange?.('');
      return;
    }
    const stored = sessionStorage.getItem(key) ?? '';
    setText(stored);
    onChange?.(stored);
  }, [sessionId]);

  // Flush any pending debounced write when the component unmounts.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (noticeTimeoutRef.current) clearTimeout(noticeTimeoutRef.current);
    };
  }, []);

  const persist = useCallback(
    (value: string) => {
      const key = samhengiStorageKey(sessionId);
      if (!key) return;
      if (value) {
        sessionStorage.setItem(key, value);
      } else {
        sessionStorage.removeItem(key);
      }
    },
    [sessionId]
  );

  const setAndNotify = useCallback(
    (value: string) => {
      setText(value);
      onChange?.(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => persist(value), STORAGE_DEBOUNCE_MS);
    },
    [onChange, persist]
  );

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAndNotify(event.target.value);
  };

  // Catch oversize paste before maxLength silently drops characters at the end.
  // Truncate explicitly and surface the truncation so the user knows.
  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = event.clipboardData.getData('text');
    if (!pasted) return;
    const target = event.currentTarget;
    const remaining = MAX_CHARS - text.length;
    if (pasted.length <= remaining) return;
    event.preventDefault();
    const truncated = pasted.slice(0, Math.max(0, remaining));
    const start = target.selectionStart ?? text.length;
    const end = target.selectionEnd ?? text.length;
    const next = text.slice(0, start) + truncated + text.slice(end);
    setAndNotify(next);
    setPasteNotice(true);
    if (noticeTimeoutRef.current) clearTimeout(noticeTimeoutRef.current);
    noticeTimeoutRef.current = setTimeout(() => setPasteNotice(false), PASTE_NOTICE_TIMEOUT_MS);
  };

  // Safety net: persist on blur regardless of debounce state, so a tab close
  // mid-typing doesn't lose the latest keystrokes.
  const handleBlur = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    persist(text);
  };

  const charCount = text.length;
  const ratio = charCount / MAX_CHARS;
  const counterClass = ratio >= 0.9 ? 'text-red-600' : ratio >= 0.8 ? 'text-amber-600' : 'text-muted-foreground';
  const hasContent = charCount > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mb-3">
      <CollapsibleTrigger className="flex items-center gap-1 font-medium text-slate-600 text-sm hover:text-slate-900">
        Samhengi
        {hasContent && (
          <span className="ml-1 inline-block h-2 w-2 rounded-full bg-amber-500">
            <span className="sr-only">inniheldur texta</span>
          </span>
        )}
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        <label htmlFor={labelId} className="sr-only">
          Samhengi fyrir samantekt
        </label>
        <Textarea
          id={labelId}
          value={text}
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={handleBlur}
          maxLength={MAX_CHARS}
          rows={5}
          placeholder="Frekari upplýsingar fyrir samantekt"
          className="resize-y text-sm"
        />
        {/* Compliance note stays visible even after the user starts typing —
            unlike a placeholder, which disappears on the first keystroke. */}
        <p className="mt-1 text-xs text-muted-foreground">
          Ekki er leyfilegt að setja inn persónugreinanlegar upplýsingar nema að vinnslusamningur og MÁP við stofnun
          leyfi slíka vinnslu.
        </p>
        <div className="mt-1 flex items-center justify-between text-xs">
          <span
            className={cn('transition-opacity', pasteNotice ? 'text-amber-700 opacity-100' : 'opacity-0')}
            role={pasteNotice ? 'status' : undefined}
            aria-live="polite"
          >
            Skerti texta umfram {MAX_CHARS} stafi
          </span>
          <span className={counterClass}>
            {charCount} / {MAX_CHARS}
          </span>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
